import type { MetricsSummary } from '../types/metrics';

export const BENEFIT_CAMPAIGN_ADMIN_PATH = '/api/benefit-campaign/admin/current';
export const BENEFIT_CAMPAIGN_ACTIVE_PATH = '/api/benefit-campaign/active';

export type BenefitCampaignResponse = {
  exists?: boolean;
  campaign?: {
    id?: string;
    name?: string;
    is_active?: boolean;
    starts_at?: string;
    ends_at?: string;
    plan_code?: string;
    duration_days?: number;
    title?: string | null;
    body?: string | null;
    status?: string;
  } | null;
};

export type BenefitCampaignForm = {
  id: string;
  name: string;
  isActive: boolean;
  startsAt: string;
  endsAt: string;
  planCode: string;
  durationDays: number;
  title: string;
  body: string;
};

export type BenefitCampaignPayload = {
  id: string;
  name: string;
  isActive: boolean;
  startsAt: string;
  endsAt: string;
  planCode: string;
  durationDays: number;
  title: string;
  body: string;
};

export const BENEFIT_CAMPAIGN_STEPS = [
  { code: 'benefit_campaign_viewed', label: '혜택 노출' },
  { code: 'benefit_campaign_login_clicked', label: '로그인 클릭' },
  { code: 'benefit_campaign_guest_clicked', label: '비로그인 선택' },
  { code: 'benefit_campaign_login_completed', label: '로그인 완료' },
  { code: 'benefit_trial_granted', label: '혜택 지급' },
  { code: 'benefit_trial_skipped', label: '지급 제외' },
];

export const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const toDateTimeInputValue = (date: Date) => {
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  return `${toDateInputValue(date)}T${time}`;
};

export const fromApiDateTimeInputValue = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.slice(0, 16);
  }
  return toDateTimeInputValue(date);
};

export const defaultBenefitCampaignForm = (): BenefitCampaignForm => {
  const now = new Date();
  const end = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return {
    id: 'first-login-pro-7d',
    name: '첫 로그인 Pro 7일 무료',
    isActive: true,
    startsAt: toDateTimeInputValue(now),
    endsAt: toDateTimeInputValue(end),
    planCode: 'SAJU_PRO',
    durationDays: 7,
    title: '기간 한정 Pro 7일 무료',
    body: '혜택 기간 안에 로그인하면 Pro 기능을 7일간 무료로 사용할 수 있어요.',
  };
};

export const benefitCampaignFormFromResponse = (
  campaign: NonNullable<BenefitCampaignResponse['campaign']>,
): BenefitCampaignForm => ({
  id: campaign.id || 'first-login-pro-7d',
  name: campaign.name || '첫 로그인 Pro 7일 무료',
  isActive: Boolean(campaign.is_active),
  startsAt: fromApiDateTimeInputValue(campaign.starts_at),
  endsAt: fromApiDateTimeInputValue(campaign.ends_at),
  planCode: campaign.plan_code || 'SAJU_PRO',
  durationDays: campaign.duration_days || 7,
  title: campaign.title || '기간 한정 Pro 7일 무료',
  body: campaign.body || '혜택 기간 안에 로그인하면 Pro 기능을 7일간 무료로 사용할 수 있어요.',
});

const parseDateTimeInput = (value: string, label: string): Date => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) {
    throw new Error(`${label}을 올바르게 입력해 주세요.`);
  }
  return date;
};

export const serializeBenefitCampaignForm = (form: BenefitCampaignForm): BenefitCampaignPayload => {
  const startsAt = parseDateTimeInput(form.startsAt, '시작일시');
  const endsAt = parseDateTimeInput(form.endsAt, '종료일시');
  const durationDays = Math.max(1, Math.floor(Number(form.durationDays) || 1));

  if (endsAt <= startsAt) {
    throw new Error('종료일시는 시작일시보다 뒤여야 합니다.');
  }

  return {
    id: form.id,
    name: form.name,
    isActive: form.isActive,
    startsAt: startsAt.toISOString(),
    endsAt: endsAt.toISOString(),
    planCode: form.planCode,
    durationDays,
    title: form.title,
    body: form.body,
  };
};

export const buildBenefitCampaignRows = (summary: MetricsSummary | null | undefined) =>
  BENEFIT_CAMPAIGN_STEPS.map((step) => {
    const item = summary?.[step.code];
    return {
      ...step,
      count: typeof item?.count === 'number' && Number.isFinite(item.count) ? item.count : 0,
      total: typeof item?.total === 'number' && Number.isFinite(item.total) ? item.total : 0,
    };
  });

export const formatMetricNumber = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '0';
  }
  return value.toLocaleString('ko-KR', { maximumFractionDigits: 2 });
};
